import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';

// Quick-search over accessible nav items — ported from the old Navbar.js's search
// (case-insensitive substring match on label/keywords, capped to 8 results, "/" to
// focus, Arrow/Enter/Escape navigation). Two presentations, same logic:
//   variant="topbar" — icon that expands into an inline input (desktop)
//   variant="drawer" — always-visible full-width input (mobile drawer)
export default function NavSearch({ variant, accessibleItems, onOpenChat, onNavigateDone }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(variant === 'drawer'); // drawer variant is always "open"
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return accessibleItems
      .filter(p => p.label.toLowerCase().includes(q) || (p.keywords || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, accessibleItems]);

  const close = useCallback(() => {
    setQuery('');
    setHighlight(0);
    if (variant === 'topbar') setOpen(false);
  }, [variant]);

  const select = useCallback((item) => {
    if (item.action === 'chat') onOpenChat?.();
    else if (item.path) navigate(item.path);
    close();
    onNavigateDone?.();
  }, [navigate, onOpenChat, close, onNavigateDone]);

  // "/" anywhere opens+focuses the topbar search (skipped while typing elsewhere)
  useEffect(() => {
    if (variant !== 'topbar') return;
    const handler = (e) => {
      if (e.key === '/' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && !e.target.isContentEditable) {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [variant]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (results[highlight]) select(results[highlight]); }
    else if (e.key === 'Escape') close();
  };

  const inputEl = (
    <div className="position-relative" style={{ width: variant === 'topbar' ? 240 : '100%' }}>
      <input
        ref={inputRef}
        className="form-control form-control-sm"
        placeholder="Search pages…"
        value={query}
        onChange={e => { setQuery(e.target.value); setHighlight(0); }}
        onKeyDown={handleKeyDown}
        style={{ backgroundColor: '#2d3f58', border: '1px solid #4a5a6a', color: '#fff', paddingRight: variant === 'topbar' ? 28 : undefined }}
      />
      {variant === 'topbar' && (
        <button
          className="btn btn-link p-0"
          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#aaa', lineHeight: 1 }}
          onClick={close}
        ><FiX size={14} /></button>
      )}
      {results.length > 0 && (
        <div className="dropdown-menu show w-100" style={{ position: 'absolute', top: '100%', left: 0, minWidth: variant === 'topbar' ? 240 : undefined, zIndex: 1100 }}>
          {results.map((p, i) => (
            <button
              key={p.id}
              className={`dropdown-item ${i === highlight ? 'active' : ''}`}
              onMouseDown={() => select(p)}
              onMouseEnter={() => setHighlight(i)}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (variant === 'drawer') return inputEl;

  return open ? inputEl : (
    <button className="btn btn-outline-light" onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} title="Search pages (press /)">
      <FiSearch size={16} />
    </button>
  );
}
